import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, ShieldCheck, X } from "lucide-react"
import type { ValidationResult } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { AutoHeading } from "@/components/automations/primitives"
import { api } from "@/lib/api"
import { automationQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/automations/$automationId/validate")({ component: Validation })

/** Screen 19. Validation runs on the server against the draft and the live connection. */
function Validation() {
  const { workspace } = useWorkspaceContext()
  const { automationId } = Route.useParams()
  const automation = useSuspenseQuery(automationQuery(workspace.slug, automationId)).data
  const params = { workspace: workspace.slug }
  const result = useQuery({ queryKey: ["workspace", workspace.slug, "automations", automationId, "validation"], queryFn: () => api<ValidationResult>(`/w/${workspace.slug}/automations/${automationId}/validate`, { method: "POST" }), staleTime: 0 })
  const rerun = useMutation({ mutationFn: () => result.refetch() })
  const data = result.data
  const blocking = data?.issues.filter((i) => i.severity === "blocking") ?? []
  const recommendations = data?.issues.filter((i) => i.severity === "recommendation") ?? []
  const passed = data?.checks.filter((c) => c.passed).length ?? 0
  const total = data?.checks.length ?? 6
  const version = automation.draft ? `Draft v${automation.draft.number}` : automation.live ? `Live v${automation.live.number}` : "Draft"
  return (
    <div className="page-wrap auto-page">
      <AutoHeading
        title="Check your draft"
        description={`${automation.name} · ${version}`}
        back={
          <Button variant="ghost" size="sm" className="mb-3 -ml-2" asChild>
            <Link to="/w/$workspace/automations/$automationId" params={{ ...params, automationId }}>
              <ArrowLeft />
              {automation.name}
            </Link>
          </Button>
        }
      >
        <Button variant="outline" onClick={() => rerun.mutate()} disabled={result.isFetching}>
          <ShieldCheck />
          Run checks again
        </Button>
      </AutoHeading>
      {!automation.draft && (
        <div className="auto-validation-banner mb-6">
          <ShieldCheck className="size-6" />
          <div className="flex-1">
            <h2>Nothing to validate</h2>
            <p>There is no draft. Edit the automation to create one.</p>
          </div>
        </div>
      )}
      {data && (
        <>
          <div className={`auto-validation-banner ${data.ok ? "" : "has-warning"}`}>
            <ShieldCheck className="size-6" />
            <div className="flex-1">
              <h2>{data.ok ? "Your draft is ready" : blocking.length === 1 ? "One thing needs your attention" : `${blocking.length} things need your attention`}</h2>
              <p>{data.ok ? `All ${total} checks passed. You can simulate the conversation before publishing.` : `Fix these issues before publishing.${automation.live ? ` Your live v${automation.live.number} continues running.` : ""}`}</p>
            </div>
            {automation.live && <Badge variant="outline">Live v{automation.live.number} active</Badge>}
          </div>
          <div className="auto-two-col mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{data.ok ? "All issues resolved" : "Blocking issues"}</CardTitle>
                <CardDescription>Clear next steps to get this version ready.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {blocking.map((issue, i) => (
                  <div key={`${issue.code}-${issue.nodeId ?? i}`} className="flex gap-4 border-b pb-6">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400">{i + 1}</span>
                    <div className="flex-1">
                      <h3 className="font-medium">{issue.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">{issue.detail}</p>
                      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-muted-foreground">{issue.location}</p>
                        {issue.connectionId ? (
                          <Button variant="outline" size="sm" asChild>
                            <Link to="/w/$workspace/channels/$connectionId" params={{ ...params, connectionId: issue.connectionId }}>
                              Review connection
                              <ArrowUpRight />
                            </Link>
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" asChild>
                            <Link to="/w/$workspace/automations/$automationId" params={{ ...params, automationId }}>
                              Open step
                              <ArrowUpRight />
                            </Link>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {blocking.length === 0 && <p className="text-sm text-muted-foreground">No blocking issues.</p>}
                {recommendations.map((issue, i) => (
                  <div key={`${issue.code}-${i}`} className="rounded-lg bg-muted/60 p-4">
                    <p className="font-medium">{issue.title}</p>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">{issue.detail}</p>
                    {issue.code === "fallback_preview" && (
                      <Button variant="link" className="mt-2 px-0" asChild>
                        <Link to="/w/$workspace/automations/$automationId/simulate" params={{ ...params, automationId }}>
                          Open simulation
                          <ArrowRight />
                        </Link>
                      </Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="self-start">
              <CardHeader>
                <CardTitle>Publish readiness</CardTitle>
                <CardDescription>
                  {passed} of {total} checks passed
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Progress value={(passed / total) * 100} className="mb-6 h-1.5" />
                <div className="space-y-5">
                  {data.checks.map((check) => (
                    <div key={check.name} className="flex items-center gap-3 text-sm">
                      {check.passed ? <Check className="size-4" /> : <X className="size-4 text-amber-600" />}
                      {check.name}
                    </div>
                  ))}
                </div>
                <p className="my-6 text-xs leading-5 text-muted-foreground">A passing draft does not guarantee every future send. Eligibility is checked again during execution.</p>
                <Button className="w-full" disabled={!data.ok} asChild={data.ok}>
                  {data.ok ? (
                    <Link to="/w/$workspace/automations/$automationId/simulate" params={{ ...params, automationId }}>
                      Continue to simulation
                      <ArrowRight />
                    </Link>
                  ) : (
                    <span>Continue to simulation</span>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  )
}
